import { addDays, cycleDayFrom, localDate, parseLocalDate } from './dates';
import type { AppMetrics, AppSettings, Exercise, PrRecord, PrResult, Routine, Session, SessionSummary, WorkoutSet } from './types';

export function effectiveLoad(set: Pick<WorkoutSet, 'weight' | 'unit'>, exercise?: Exercise, bodyWeight = 0): number {
  const isBodyweight = exercise?.loadType === 'bodyweight' || (!exercise && set.unit === 'bodyweight');
  return Math.max(0, isBodyweight ? bodyWeight + set.weight : set.weight);
}

export function setVolume(set: WorkoutSet, exercise?: Exercise, bodyWeight = 0): number {
  if (!set.completedAt || set.type === 'warmup') return 0;
  return effectiveLoad(set, exercise, bodyWeight) * Math.max(0, set.reps);
}

export function estimatedOneRepMax(weight: number, reps: number): number {
  if (weight <= 0 || reps <= 0) return 0;
  if (reps === 1) return weight;
  if (reps > 12) return 0;
  return weight * (1 + reps / 30);
}

export function evaluatePr(candidate: WorkoutSet, history: WorkoutSet[], exercise?: Exercise, bodyWeight = 0): PrResult {
  const prior = history.filter(set => set.id !== candidate.id && set.completedAt && set.type !== 'warmup' && set.exerciseId === candidate.exerciseId && set.reps > 0);
  if (candidate.type === 'warmup' || candidate.reps <= 0 || prior.length === 0) return { weightPr: false, repPr: false, e1rmPr: false };
  const load = effectiveLoad(candidate, exercise, bodyWeight);
  const maxWeight = Math.max(0, ...prior.map(set => effectiveLoad(set, exercise, bodyWeight)));
  const repsAtEqualOrGreater = prior.filter(set => effectiveLoad(set, exercise, bodyWeight) >= load);
  const maxComparableReps = repsAtEqualOrGreater.length ? Math.max(...repsAtEqualOrGreater.map(set => set.reps)) : undefined;
  const e1rm = estimatedOneRepMax(load, candidate.reps);
  const maxE1rm = Math.max(0, ...prior.map(set => estimatedOneRepMax(effectiveLoad(set, exercise, bodyWeight), set.reps)));
  return {
    weightPr: load > maxWeight,
    repPr: maxComparableReps !== undefined && candidate.reps > maxComparableReps,
    e1rmPr: e1rm > 0 && e1rm > maxE1rm
  };
}

export function prLabels(result: PrResult): Array<PrRecord['kind']> {
  return [result.weightPr ? 'Weight PR' : undefined, result.repPr ? 'Rep PR' : undefined, result.e1rmPr ? 'Estimated 1RM PR' : undefined].filter(Boolean) as Array<PrRecord['kind']>;
}

export function replayPersonalRecords(sessions: Session[], sets: WorkoutSet[], exercises: Exercise[], bodyWeight = 0): PrRecord[] {
  const validSessionIds = new Set(sessions.filter(session => session.finishedAt).map(session => session.id));
  const sessionMap = new Map(sessions.map(session => [session.id, session]));
  const exerciseMap = new Map(exercises.map(exercise => [exercise.id, exercise]));
  const history: WorkoutSet[] = [];
  const emitted = new Set<string>();
  const records: PrRecord[] = [];
  const completed = sets.filter(set => set.completedAt && validSessionIds.has(set.sessionId)).sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  for (const candidate of completed) {
    const exercise = exerciseMap.get(candidate.exerciseId);
    const result = evaluatePr(candidate, history, exercise, bodyWeight);
    for (const kind of prLabels(result)) {
      const dedupe = `${candidate.sessionId}:${candidate.exerciseId}:${kind}`;
      if (emitted.has(dedupe)) continue;
      emitted.add(dedupe);
      const load = effectiveLoad(candidate, exercise, bodyWeight);
      records.push({
        id: `${candidate.id}:${kind}`,
        sessionId: candidate.sessionId,
        exerciseId: candidate.exerciseId,
        date: sessionMap.get(candidate.sessionId)?.localDate || localDate(new Date(candidate.completedAt || Date.now())),
        kind,
        value: kind === 'Estimated 1RM PR' ? estimatedOneRepMax(load, candidate.reps) : kind === 'Rep PR' ? candidate.reps : load,
        weight: load,
        reps: candidate.reps
      });
    }
    history.push(candidate);
  }
  return records;
}

export function detectPersonalRecord(candidate: WorkoutSet, history: WorkoutSet[], exercise?: Exercise, bodyWeight = 0): string[] {
  return prLabels(evaluatePr(candidate, history, exercise, bodyWeight));
}

export function startOfWeek(timestamp = Date.now()): number {
  const date = new Date(timestamp);
  const day = date.getDay();
  const difference = day === 0 ? 6 : day - 1;
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - difference);
  return date.getTime();
}

export function plannedTrainingStreak(sessions: Session[], sets: WorkoutSet[], routines: Routine[], now = Date.now()): number {
  const sessionHasWork = new Set(sets.filter(set => set.completedAt).map(set => set.sessionId));
  const completed = sessions.filter(session => session.finishedAt && sessionHasWork.has(session.id)).sort((a, b) => a.localDate.localeCompare(b.localDate) || a.startedAt - b.startedAt);
  const latest = completed.at(-1);
  if (!latest) return 0;
  const latestRoutine = routines.find(routine => routine.id === latest.routineId);
  if (!latestRoutine) return 0;
  const today = localDate(new Date(now));
  const completedByDate = new Map(completed.map(session => [session.localDate, session]));
  for (let date = addDays(parseLocalDate(latest.localDate), 1), offset = 1; localDate(date) < today; date = addDays(date, 1), offset += 1) {
    const planned = routines.find(routine => routine.dayOfCycle === cycleDayFrom(latestRoutine.dayOfCycle, offset));
    if (planned?.exerciseRefs.length && !completedByDate.has(localDate(date))) return 0;
  }
  let streak = 1;
  for (let date = addDays(parseLocalDate(latest.localDate), -1), offset = -1; ; date = addDays(date, -1), offset -= 1) {
    const planned = routines.find(routine => routine.dayOfCycle === cycleDayFrom(latestRoutine.dayOfCycle, offset));
    if (!planned) break;
    if (planned.exerciseRefs.length === 0) continue;
    const session = completedByDate.get(localDate(date));
    if (!session) break;
    streak += 1;
  }
  return streak;
}

export function computeMetrics(sessions: Session[], sets: WorkoutSet[], now = Date.now(), exercises: Exercise[] = [], routines: Routine[] = [], settings?: AppSettings, bodyWeight = 0): AppMetrics {
  const workSessionIds = new Set(sets.filter(set => set.completedAt).map(set => set.sessionId));
  const completed = sessions.filter(session => Boolean(session.finishedAt) && workSessionIds.has(session.id)).sort((a, b) => a.startedAt - b.startedAt);
  const completedIds = new Set(completed.map(session => session.id));
  const completedSets = sets.filter(set => completedIds.has(set.sessionId) && Boolean(set.completedAt));
  const weekStart = startOfWeek(now);
  const weeklyIds = new Set(completed.filter(session => (session.finishedAt || 0) >= weekStart).map(session => session.id));
  const weeklySets = completedSets.filter(set => weeklyIds.has(set.sessionId));
  const exerciseMap = new Map(exercises.map(exercise => [exercise.id, exercise]));
  return {
    completedSessions: completed.length,
    weeklySessions: weeklyIds.size,
    weeklySets: weeklySets.length,
    weeklyVolume: weeklySets.reduce((sum, set) => sum + setVolume(set, exerciseMap.get(set.exerciseId), bodyWeight), 0),
    streak: routines.length ? plannedTrainingStreak(completed, completedSets, routines, now) : 0,
    prCount: replayPersonalRecords(completed, completedSets, exercises, bodyWeight).length
  };
}

export function summarizeSession(session: Session, sets: WorkoutSet[], allSets: WorkoutSet[], exercises: Exercise[] = [], routineName = 'Workout', bodyWeight = 0): SessionSummary {
  const exerciseMap = new Map(exercises.map(exercise => [exercise.id, exercise]));
  const completed = sets.filter(set => set.sessionId === session.id && set.completedAt).sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  const prior = allSets.filter(set => set.sessionId !== session.id && set.completedAt);
  const emitted = new Set<string>();
  let prCount = 0;
  for (const candidate of completed) {
    const result = evaluatePr(candidate, prior, exerciseMap.get(candidate.exerciseId), bodyWeight);
    for (const kind of prLabels(result)) {
      const key = `${candidate.exerciseId}:${kind}`;
      if (!emitted.has(key)) {
        emitted.add(key);
        prCount += 1;
      }
    }
    prior.push(candidate);
  }
  const lines = [...new Set(completed.map(set => set.exerciseId))].map(exerciseId => {
    const exerciseSets = completed.filter(set => set.exerciseId === exerciseId);
    const exercise = exerciseMap.get(exerciseId);
    const best = [...exerciseSets].sort((a, b) => effectiveLoad(b, exercise, bodyWeight) - effectiveLoad(a, exercise, bodyWeight) || b.reps - a.reps)[0];
    const bestLoad = best ? effectiveLoad(best, exercise, bodyWeight) : 0;
    return {
      exerciseId,
      exerciseName: exercise?.name || exerciseId,
      sets: exerciseSets.length,
      volume: exerciseSets.reduce((sum, set) => sum + setVolume(set, exercise, bodyWeight), 0),
      best: best ? `${exercise?.loadType === 'bodyweight' ? `BW${best.weight ? `+${formatWeight(best.weight)}` : ''}` : formatWeight(bestLoad)} x ${best.reps}` : ''
    };
  });
  return {
    sessionId: session.id,
    routineName,
    durationSeconds: Math.max(0, Math.round(((session.finishedAt || Date.now()) - session.startedAt) / 1000)),
    completedSets: completed.length,
    volume: lines.reduce((sum, line) => sum + line.volume, 0),
    prs: prCount,
    lines
  };
}

export function nextSessionTarget(lastSets: WorkoutSet[], repMax: number, increment = 5): { weight: number; reps: number; rationale: string } | undefined {
  const working = lastSets.filter(set => set.completedAt && set.type !== 'warmup').sort((a, b) => a.setNumber - b.setNumber);
  if (working.length === 0) return undefined;
  const allHitTop = working.every(set => set.reps >= repMax);
  const weight = working[0].weight;
  if (allHitTop) return { weight: weight + increment, reps: Math.max(1, repMax - 2), rationale: `Add ${increment} lb because every working set reached the top of the range.` };
  const lowestReps = Math.min(...working.map(set => set.reps));
  return { weight, reps: Math.min(repMax, lowestReps + 1), rationale: 'Keep the load and add one rep before increasing weight.' };
}

export function stalledExercises(sessions: Session[], sets: WorkoutSet[], exercises: Exercise[], bodyWeight = 0): string[] {
  const finished = sessions.filter(session => session.finishedAt).sort((a, b) => (a.finishedAt || 0) - (b.finishedAt || 0));
  const exerciseMap = new Map(exercises.map(exercise => [exercise.id, exercise]));
  const stalled: string[] = [];
  for (const exercise of exercises) {
    const points = finished.map(session => {
      const values = sets.filter(set => set.sessionId === session.id && set.exerciseId === exercise.id && set.completedAt && set.type !== 'warmup');
      return Math.max(0, ...values.map(set => estimatedOneRepMax(effectiveLoad(set, exerciseMap.get(exercise.id), bodyWeight), set.reps)));
    }).filter(Boolean);
    const recent = points.slice(-3);
    if (recent.length === 3 && Math.max(...recent.slice(1)) <= recent[0] + 0.01) stalled.push(exercise.id);
  }
  return stalled;
}

export function plateBreakdown(totalWeight: number, barWeight: number, inventory: number[]): number[] {
  let perSide = Math.max(0, (totalWeight - barWeight) / 2);
  const plates: number[] = [];
  for (const plate of [...inventory].sort((a, b) => b - a)) {
    while (perSide + 0.0001 >= plate) {
      plates.push(plate);
      perSide -= plate;
    }
  }
  return plates;
}

export function nearestPlateWeight(target: number, barWeight: number, inventory: number[]): number {
  const increment = Math.max(10, Math.min(...inventory.filter(value => value > 0)) * 2);
  return Math.max(barWeight, Math.round((target - barWeight) / increment) * increment + barWeight);
}

export function warmupPlan(workingWeight: number, barWeight: number, inventory: number[]): Array<{ weight: number; reps: number }> {
  if (workingWeight < 95) return [];
  return [
    { weight: barWeight, reps: 10 },
    { weight: nearestPlateWeight(workingWeight * 0.4, barWeight, inventory), reps: 8 },
    { weight: nearestPlateWeight(workingWeight * 0.6, barWeight, inventory), reps: 5 },
    { weight: nearestPlateWeight(workingWeight * 0.8, barWeight, inventory), reps: 3 }
  ];
}

export function muscleVolume(sets: WorkoutSet[], exercises: Exercise[]): Map<string, number> {
  const exerciseMap = new Map(exercises.map(item => [item.id, item]));
  const result = new Map<string, number>();
  for (const set of sets.filter(item => Boolean(item.completedAt) && item.type !== 'warmup')) {
    for (const muscle of exerciseMap.get(set.exerciseId)?.muscles || []) result.set(muscle, (result.get(muscle) || 0) + 1);
  }
  return result;
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.max(0, seconds % 60)).padStart(2, '0')}`;
}

export function formatWeight(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}

export function fmtCount(value: number, singular: string, plural = `${singular}s`): string {
  return `${value} ${value === 1 ? singular : plural}`;
}

export function monthlyRecap(sessions: Session[], sets: WorkoutSet[], exercises: Exercise[] = [], bodyWeight = 0, date = new Date()): { label: string; sessions: number; sets: number; volume: number; prs: number; liftDeltas: Array<{ exerciseId: string; delta: number }> } {
  const prefix = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  const finished = sessions.filter(session => Boolean(session.finishedAt)).sort((a, b) => a.startedAt - b.startedAt);
  const monthIds = new Set(finished.filter(session => session.localDate.startsWith(prefix)).map(session => session.id));
  const completed = sets.filter(set => Boolean(set.completedAt) && set.type !== 'warmup').sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  const exerciseMap = new Map(exercises.map(exercise => [exercise.id, exercise]));
  const monthSets = completed.filter(set => monthIds.has(set.sessionId));
  const deltas = [...new Set(monthSets.map(set => set.exerciseId))].map(exerciseId => {
    const points = monthSets.filter(set => set.exerciseId === exerciseId).map(set => estimatedOneRepMax(effectiveLoad(set, exerciseMap.get(exerciseId), bodyWeight), set.reps)).filter(Boolean);
    return { exerciseId, delta: points.length > 1 ? points.at(-1)! - points[0] : 0 };
  }).filter(item => item.delta !== 0).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 3);
  return {
    label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    sessions: monthIds.size,
    sets: monthSets.length,
    volume: monthSets.reduce((sum, set) => sum + setVolume(set, exerciseMap.get(set.exerciseId), bodyWeight), 0),
    prs: replayPersonalRecords(finished, completed, exercises, bodyWeight).filter(record => monthIds.has(record.sessionId)).length,
    liftDeltas: deltas
  };
}
